import React, { PropsWithChildren, useLayoutEffect, useRef } from 'react';
import styled, { css } from 'styled-components';

import { Icon } from '@column/column-ui-kit';

import { useNavigate } from '~/lib/navigation';
import { useScrollStore } from '~/stores/Scroll';
import { Inner } from '~/styles';

interface PageHeaderProps {
  text: string;
  decoration?: React.ReactNode;
  borderOnScroll?: boolean;
  border?: boolean;
  isScrolled?: boolean;
  tabs?: React.ReactNode;
  backButton?: boolean;
  backPath?: string;
}

const Wrapper = styled.div<PageHeaderProps>`
  position: sticky;
  top: 0;
  z-index: 4;
  display: grid;
  background: ${({ theme }) => theme.background};
  padding: 0 0 16px;
  border-bottom: 1px solid ${({ theme }) => theme.gray.blendToBackground(150)};
  transition: border-color 0.1s ease-in-out;

  ${({ border }) =>
    border === false &&
    css`
      border-bottom: 0;
    `};

  ${({ borderOnScroll }) =>
    borderOnScroll &&
    css`
      margin-bottom: 1px;
      border-bottom: 1px solid transparent;
    `};

  ${({ isScrolled, borderOnScroll }) =>
    isScrolled === true &&
    borderOnScroll === true &&
    css`
      border-color: ${({ theme }) => theme.gray.blendToBackground(150)};
    `};
`;

const Grid = styled.div`
  display: grid;
  align-items: flex-start;
  position: relative;
  z-index: 1;
  grid-template-columns: 1fr;

  @media (min-width: ${({ theme }) => theme.breakpoints.tablet}) {
    grid-template-columns: auto min-content;
  }
`;

const PageTitle = styled.h1<{ isExiting?: boolean }>`
  font-size: 16px;
  line-height: 20px;
  font-weight: 500;
  letter-spacing: -0.02em;
  margin: 0;
  display: flex;
  align-items: center;
  flex: auto;
  max-height: 20px;

  span {
    display: block;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
    max-width: 75%;
  }

  opacity: 1;
  animation-name: animateIn;
  animation-iteration-count: 1;
  animation-timing-function: cubic-bezier(0.25, 0.46, 0.45, 0.94);
  animation-duration: 0.3s;
  transition: all 0.3s cubic-bezier(0.25, 0.46, 0.45, 0.94);

  @keyframes animateIn {
    0% {
      opacity: 0;
      transform: translateX(-4px);
    }
    100% {
      opacity: 1;
      transform: translateX(0);
    }
  }

  ${({ isExiting }) =>
    isExiting &&
    css`
      opacity: 0;
    `}
`;

const PageTitleDecoration = styled.div`
  margin-left: 8px;
`;

const BackButton = styled.button<{ isExiting?: boolean; disabled?: boolean }>`
  display: flex;
  align-items: center;
  justify-content: center;
  background: none;
  border: none;
  color: ${({ theme }) => theme.secondary.blendToBackground(500)};
  padding: 4px;
  cursor: pointer;
  transition: all 0.2s cubic-bezier(0.25, 0.46, 0.45, 0.94);
  width: 32px;
  margin-left: -8px;
  opacity: 1;

  ${({ isExiting }) =>
    isExiting &&
    css`
      opacity: 0;
      width: 0;
    `}

  ${({ disabled }) =>
    disabled &&
    css`
      pointer-events: none;
      cursor: default;
    `}

  &:hover {
    color: ${({ theme }) => theme.secondary.background};
  }
`;

const Actions = styled.div`
  display: grid;
  grid-template-columns: auto;
  max-height: none;
  gap: 8px;
  margin-top: 8px;

  > * {
    width: 100%;
    max-width: 100%;
    margin-top: 0;

    button {
      width: 100%;
    }
  }

  @media (min-width: 400px) {
    grid-template-columns: repeat(auto-fit, minmax(0, 1fr));
  }

  @media (min-width: ${({ theme }) => theme.breakpoints.tablet}) {
    display: flex;
    align-items: center;
    max-height: 20px;
    margin-top: 0;
  }

  @media (max-width: ${({ theme }) => theme.breakpoints.mobile}) {
    .hide-mobile {
      display: none;
    }
  }
`;

const Tabs = styled.div`
  background: ${({ theme }) => theme.secondary.blendToBackground(25)};
  width: 100%;
  margin-bottom: -17px;
  border-top: 1px solid ${({ theme }) => theme.gray.blendToBackground(150)};
  box-shadow: 0 -1px 0 0 ${({ theme }) => theme.gray.blendToBackground(150)} inset;
  margin-top: 16px;
  box-sizing: border-box;

  @media (min-width: ${({ theme }) => theme.breakpoints.tablet}) {
    padding: 0 var(--page-margin);
    overflow: hidden;
  }
`;

export const PageHeader: React.FC<PropsWithChildren<PageHeaderProps>> = (props) => {
  const wrapperRef = useRef<HTMLDivElement>(null);
  const pageFrameScroll = useScrollStore((s) => s.pageFrameScroll);
  const navigate = useNavigate();
  const [isExiting, setIsExiting] = React.useState(false);

  const backButton = props.backButton || !!props.backPath;

  useLayoutEffect(() => {
    const ref = wrapperRef.current;

    if (!ref) {
      return;
    }

    ref.scrollTo({ top: 0 });
  }, [wrapperRef]);

  const handleBackClick = () => {
    if (!backButton) return;
    setIsExiting(true);
    setTimeout(() => {
      if (props.backPath) {
        navigate(props.backPath);
      } else {
        navigate(-1);
      }
    }, 200);
  };

  const hasActions = React.Children.toArray(props.children).filter(Boolean).length > 0;

  return (
    <Wrapper {...props} isScrolled={!!(pageFrameScroll ?? 0 > 0)}>
      <Inner pt={16} px="var(--page-margin)" pb={0}>
        <Grid>
          <PageTitle isExiting={isExiting}>
            {backButton && (
              <BackButton
                onClick={handleBackClick}
                isExiting={!backButton || isExiting}
                disabled={!backButton || isExiting}
              >
                <Icon.ArrowLeft />
              </BackButton>
            )}
            <span data-cy={`Header: ${props.text}`}>{props.text}</span>
            {props.decoration ? <PageTitleDecoration>{props.decoration}</PageTitleDecoration> : null}
          </PageTitle>
          {hasActions && <Actions>{props.children}</Actions>}
        </Grid>
      </Inner>
      {props.tabs && <Tabs>{props.tabs}</Tabs>}
    </Wrapper>
  );
};
